import { useEffect, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { formatBytes, sha256Hex } from "@/hashtree/bytes";
import type { PickedFile } from "@/hashtree/useHashtreeBuild";
import { Field, FieldGroup } from "@/components/HashDisplay";
import { Slide, SpecNote } from "./Slide";

const SAMPLE_TEXT = `# Hashtree

A hashtree stores a directory as ordinary Blossom blobs.

Every node is content addressed: the bytes decide the hash, and the
hash decides the address. Change one byte anywhere in this file and
every hash from here up to the root changes with it.

That is what makes the structure verifiable. A reader that knows the
root hash can check every blob it fetches on the way down, and can
never be handed different bytes than the ones the tree committed to.
`;

export function PickFileSlide({
  file,
  onPick,
  onNext,
}: {
  file: PickedFile | null;
  onPick: (file: PickedFile) => void;
  onNext: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [plaintextHash, setPlaintextHash] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPlaintextHash(null);
      return;
    }
    let cancelled = false;
    void sha256Hex(file.bytes).then((hash) => {
      if (!cancelled) setPlaintextHash(hash);
    });
    return () => {
      cancelled = true;
    };
  }, [file]);

  const accept = async (picked: File) => {
    onPick({
      name: picked.name,
      bytes: new Uint8Array(await picked.arrayBuffer()),
      type: picked.type || "application/octet-stream",
    });
  };

  const useSample = () => {
    onPick({
      name: "hashtree.md",
      bytes: new TextEncoder().encode(SAMPLE_TEXT),
      type: "text/markdown",
    });
  };

  return (
    <Slide
      title="Pick a file"
      lede="Everything downstream is derived from these bytes. Anything works — a small text file makes the chunk boundaries easiest to read."
    >
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const dropped = event.dataTransfer.files[0];
          if (dropped) void accept(dropped);
        }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
          dragging ? "border-primary bg-primary/5" : "hover:border-muted-foreground/40 hover:bg-muted/30"
        }`}
      >
        <Upload className="mx-auto mb-2 size-6 text-muted-foreground" />
        <p className="text-sm font-medium">Drop a file, or click to choose one</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Nothing is uploaded yet — this stays in your browser until the upload step.
        </p>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={(event) => {
            const chosen = event.target.files?.[0];
            if (chosen) void accept(chosen);
          }}
        />
      </div>

      <button
        type="button"
        onClick={useSample}
        className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
      >
        or use a sample file
      </button>

      {file && (
        <>
          <FieldGroup>
            <Field label="name" value={file.name} />
            <Field label="size" value={`${file.bytes.length} bytes (${formatBytes(file.bytes.length)})`} copyable={false} />
            <Field label="media type" value={file.type} />
            <Field
              label="SHA256"
              value={plaintextHash ?? "computing…"}
              tone="hash"
              hint="Of the plaintext. BUD-15 will use exactly this value as the encryption key."
            />
          </FieldGroup>

          <SpecNote>
            That plaintext hash is not the address this file gets stored under. Once it is encrypted, the blob is
            addressed by the hash of the <em>ciphertext</em> instead — the next slide shows both side by side.
          </SpecNote>

          <button
            type="button"
            onClick={onNext}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
          >
            Choose how to split it
          </button>
        </>
      )}
    </Slide>
  );
}
